/**
 * De landingsplek van een sessieoverdracht.
 *
 * Een beheerder die in de beheeromgeving op "ga verder naar het dashboard" drukt,
 * komt hier aan met een eenmalig token. Dit scherm wisselt dat in voor een sessie op
 * DEZE hostnaam en stuurt hem door naar het overzicht.
 *
 * ## Waarom het token in de fragmentidentifier staat
 *
 * `#token` en niet `?token`. Alles achter het hekje wordt door de browser NIET
 * meegestuurd naar de server: het staat niet in de toegangslogboeken, niet in de
 * `Referer` van een volgende aanvraag, en niet in de logs van een tussenliggende
 * proxy. Voor een geheim dat door een adresbalk reist, is dat het verschil tussen
 * "kortstondig zichtbaar" en "voorgoed vastgelegd" (§17.43, ADR-144).
 *
 * Het token wordt daarna meteen uit de adresbalk gehaald. Het is dan al verbruikt —
 * de database markeert het bij het innemen — maar een geheim laten staan waar iemand
 * overheen kan kijken, heeft geen enkel nut.
 */
import { useEffect, useRef, useState } from 'react'
import type { ReactElement } from 'react'
import { useNavigate } from 'react-router'

import { claimHandover } from '../lib/auth-client'
import { useTranslate } from '../lib/i18n-provider'
import { useSession } from '../lib/session-context'

export function HandoverPage(): ReactElement {
  const t = useTranslate()
  const navigate = useNavigate()
  const { refresh } = useSession()
  const [failed, setFailed] = useState(false)
  const claimedRef = useRef(false)

  useEffect(() => {
    // Eén keer, ook onder StrictMode: het token is eenmalig, en een tweede poging
    // zou aantoonbaar mislukken en een foutmelding tonen die er niet hoort.
    if (claimedRef.current) {
      return
    }
    claimedRef.current = true

    const token = decodeURIComponent(window.location.hash.replace(/^#/, ''))
    // Het geheim uit de adresbalk halen voordat er iets anders gebeurt.
    window.history.replaceState(null, '', window.location.pathname)

    void (async () => {
      if (token === '') {
        setFailed(true)
        return
      }
      const result = await claimHandover(token)
      if (!result.ok) {
        setFailed(true)
        return
      }
      // De sessie bestaat nu; de context moet hem nog ophalen voordat het overzicht
      // rendert, anders stuurt ProtectedRoute door naar /login.
      await refresh()
      void navigate('/', { replace: true })
    })()
  }, [navigate, refresh])

  return (
    <section>
      <h1>{t('dashboard.handover.title')}</h1>
      {failed ? (
        <>
          <p role="alert">{t('dashboard.handover.failed')}</p>
          <p>{t('dashboard.handover.failedHint')}</p>
        </>
      ) : (
        <p>{t('dashboard.handover.working')}</p>
      )}
    </section>
  )
}
